import { createContext, useContext, useEffect, useState } from 'react';
import secureLocalStorage from 'react-secure-storage';
import { axiosInstance } from './pages/Config';

const RolesContext = createContext();

export const usePermissionData = () => useContext(RolesContext);

export const AppContext = ({ children }) => {

    const credential = secureLocalStorage.getItem("admin_credentials")

    const [loginStatus, setLoginStatus] = useState(
        !!(credential?.token && credential?.email)
    )

    const [permissions, setPermissions] = useState(
        () => secureLocalStorage.getItem("user_permissions") ?? null
    )

    // Fetches and stores permissions for the logged-in staff member's role.
    // Super Admin gets null (full access). Regular roles get their permission array.
    const loadPermissions = async (roleName) => {
        if (!roleName || roleName === 'Super Admin') {
            secureLocalStorage.removeItem("user_permissions")
            setPermissions(null)
            return
        }
        try {
            const res = await axiosInstance.get('roles')
            const roles = res.data?.data ?? res.data ?? []
            const matched = roles.find(r => (r.roleName ?? r.name) === roleName)
            if (matched?.id) {
                const detail = await axiosInstance.get(`roles/${matched.id}`)
                const perms = detail.data?.data?.permissions ?? detail.data?.permissions ?? []
                secureLocalStorage.setItem("user_permissions", perms)
                setPermissions(perms)
            }
        } catch (err) {
            console.error('Failed to load permissions', err)
        }
    }

    // moduleId: matches the IDs defined in AddRole.js (1=Dashboard, 2=Videos, etc.)
    // type: 1=view, 2=add, 3=edit, 4=delete
    const checkAccess = (moduleId, type) => {
        const cred = secureLocalStorage.getItem("admin_credentials")
        if (!cred?.token) return false
        // Super Admin always has full access
        if (cred.role === 'Super Admin') return true
        if (!permissions || !Array.isArray(permissions)) return false
        const perm = permissions.find(p => p.moduleId === moduleId)
        if (!perm) return false
        switch (type) {
            case 1: return perm.view === 1
            case 2: return perm.add === 1
            case 3: return perm.edit === 1
            case 4: return perm.delete === 1
            default: return false
        }
    }

    const clearPermissions = () => {
        secureLocalStorage.removeItem("user_permissions")
        setPermissions(null)
    }

    // On every page refresh, re-fetch permissions from the server so that
    // any role changes made by the admin are picked up without requiring re-login.
    useEffect(() => {
        const cred = secureLocalStorage.getItem("admin_credentials")
        if (cred?.token && cred?.role && cred.role !== 'Super Admin') {
            loadPermissions(cred.role)
        }
    }, [])

    return (
        <RolesContext.Provider value={{
            checkAccess,
            loginStatus,
            setLoginStatus,
            loadPermissions,
            clearPermissions,
        }}>
            {children}
        </RolesContext.Provider>
    )
}
